/**
 * Shared icon-map — Single source of truth for all icon resolution.
 *
 * Used by Sidebar, CommandPalette, MDITabs, and any future component.
 * Icons are lazily resolved from the Lucide icon set.
 */

import {
  Briefcase, Users, Receipt, Ship, Package, Globe,
  MessageSquare, Settings, ScrollText, Bell, Layout,
  FileText, BarChart3, AtSign, Mail, MessageCircle,
  Target, GitBranch, ListOrdered, Cpu, CheckCircle,
  Building, UserCog, LayoutDashboard, Search, Plus,
  Edit, Trash, Check, X, ChevronRight, ChevronDown,
  Menu, LogOut, HelpCircle, Palette, Sun, Moon,
  Monitor, Truck, Plane, DollarSign, CreditCard,
  Warehouse, Home, Inbox, Command, Eye, EyeOff,
  ArrowRight, Shield,
  Zap, Bot, Filter, Terminal, Anchor, MapPin,
  PlaneLanding, BarChart2, TrendingUp, ShieldCheck,
  UserCheck, Plug, Send, Activity, Banknote, Clock,
  Navigation, Route, BookOpen, Pencil, Database,
  CheckSquare, ShoppingCart, Building2, Map, CornerUpLeft,
  Lock, AlertTriangle, FileMinus, PackageOpen, Archive,
  ListChecks, ClipboardList, Layers, Tag, Percent, Radar,
  Box, Container, Network, UsersRound, FileSearch, Wand2, Forklift,
  Wrench, Calculator, CircleDot, Settings2, Rocket,
  type LucideIcon,
} from 'lucide-react';
import { createElement, type ComponentType, type CSSProperties } from 'react';
import type { BooxIconSetId } from '@/lib/visual-preferences';

export type BooxIconComponent = ComponentType<Record<string, unknown> & { className?: string }>;

/**
 * Master icon registry — maps string names to Lucide components.
 * Entity Registry `icon` field values are resolved here.
 */
const ICON_REGISTRY: Record<string, LucideIcon> = {
  // Navigation / Layout
  home: Home,
  inbox: Inbox,
  dashboard: LayoutDashboard,
  'layout-dashboard': LayoutDashboard,
  layout: Layout,
  menu: Menu,
  search: Search,

  // Business
  briefcase: Briefcase,
  truck: Truck,
  ship: Ship,
  plane: Plane,
  package: Package,
  warehouse: Warehouse,
  container: Container,
  forklift: Forklift,
  'undo-2': Package,
  camera: Package,

  // People
  users: Users,
  user: Users,
  contact: Users,
  building: Building,
  'user-cog': UserCog,

  // Finance
  receipt: Receipt,
  'credit-card': CreditCard,
  'dollar-sign': DollarSign,
  landmark: Receipt,
  'arrow-left-right': Receipt,
  list: Receipt,

  // Communication
  'message-square': MessageSquare,
  'message-circle': MessageCircle,
  mail: Mail,
  'at-sign': AtSign,
  bell: Bell,
  paperclip: Package,

  // Config / System
  settings: Settings,
  hash: Settings,
  'settings-2': Settings2,
  scale: Settings,
  globe: Globe,
  cpu: Cpu,
  command: Command,

  // Status / Actions
  'scroll-text': ScrollText,
  'file-text': FileText,
  'bar-chart-3': BarChart3,
  target: Target,
  'git-branch': GitBranch,
  'list-ordered': ListOrdered,
  'check-circle': CheckCircle,
  check: Check,
  plus: Plus,
  edit: Edit,
  trash: Trash,
  x: X,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  'log-out': LogOut,
  'help-circle': HelpCircle,
  activity: ScrollText,

  // UI
  palette: Palette,
  sun: Sun,
  moon: Moon,
  monitor: Monitor,
  eye: Eye,
  'eye-off': EyeOff,
  'arrow-right': ArrowRight,
  shield: Shield,

  // AI / Command
  zap: Zap,
  bot: Bot,
  wand: Wand2,
  filter: Filter,
  terminal: Terminal,
  anchor: Anchor,
  'map-pin': MapPin,
  'plane-landing': PlaneLanding,
  'bar-chart-2': BarChart2,
  'trending-up': TrendingUp,
  'shield-check': ShieldCheck,
  'shield-alert': AlertTriangle,
  'user-check': UserCheck,
  plug: Plug,
  send: Send,
  banknote: Banknote,
  clock: Clock,
  navigation: Navigation,
  route: Route,
  'book-open': BookOpen,
  pencil: Pencil,
  'edit-2': Pencil,
  database: Database,

  // Sidebar nav (added for restructured nav)
  'check-square': CheckSquare,
  'shopping-cart': ShoppingCart,
  'building-2': Building2,
  map: Map,
  'corner-up-left': CornerUpLeft,
  lock: Lock,
  'alert-triangle': AlertTriangle,
  'file-minus': FileMinus,
  'package-open': PackageOpen,
  archive: Archive,
  'list-checks': ListChecks,
  'clipboard-list': ClipboardList,
  layers: Layers,
  tag: Tag,
  percent: Percent,
  radar: Radar,
  box: Box,
  network: Network,
  'users-round': UsersRound,
  'file-search': FileSearch,
  wrench: Wrench,
  calculator: Calculator,
  'circle-dot': CircleDot,
  rocket: Rocket,
};

/**
 * Resolve an icon name to a Lucide component.
 * Falls back to `Briefcase` if not found.
 */
export function resolveIcon(name: string | undefined | null): LucideIcon {
  if (!name) return Briefcase;
  return ICON_REGISTRY[name] ?? Briefcase;
}

const themedIconCache = new globalThis.Map<string, BooxIconComponent>();

function toThemedIcon(BaseIcon: LucideIcon, iconSet: BooxIconSetId): BooxIconComponent {
  if (iconSet === 'lucide') return BaseIcon;
  const cacheKey = `${iconSet}:${BaseIcon.displayName ?? BaseIcon.name ?? 'icon'}`;
  const cached = themedIconCache.get(cacheKey);
  if (cached) return cached;

  const ThemedIcon: BooxIconComponent = ({ className, style, ...props }) => {
    const IconComponent = BaseIcon as ComponentType<Record<string, unknown>>;
    const iconStyle = (style && typeof style === 'object' ? style : {}) as CSSProperties;
    if (iconSet === 'phosphor') {
      return createElement(IconComponent, {
        ...props,
        className,
        strokeWidth: 1.8,
        style: {
          ...iconStyle,
          fill: 'currentColor',
          fillOpacity: 0.14,
        },
      });
    }

    if (iconSet === 'streamline_free') {
      return createElement(IconComponent, {
        ...props,
        className,
        strokeWidth: 2.4,
        style: {
          ...iconStyle,
          filter: 'drop-shadow(0 1px 0 rgba(255,255,255,0.55))',
        },
      });
    }

    return createElement(IconComponent, { ...props, className, strokeWidth: 1.65, style: iconStyle });
  };

  ThemedIcon.displayName = `Boox${iconSet}Icon`;
  themedIconCache.set(cacheKey, ThemedIcon);
  return ThemedIcon;
}

export function resolveThemedIcon(name: string | undefined | null, iconSet: BooxIconSetId): BooxIconComponent {
  return toThemedIcon(resolveIcon(name), iconSet);
}

/**
 * Category icons — maps entity category names to icons.
 * Auto-generated from DynamicConfig when available, otherwise uses defaults.
 */
export const DEFAULT_CATEGORY_ICONS: Record<string, LucideIcon> = {
  transaction: Briefcase,
  master: BookOpen,
  communication: MessageSquare,
  agent: Cpu,
  config: ListChecks,
  event: Bell,
  attachment: FileText,
};

export function resolveCategoryIcon(category: string): LucideIcon {
  return DEFAULT_CATEGORY_ICONS[category] ?? Briefcase;
}

export type { LucideIcon };
